var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import './DataDictionaryTable.scss';
import classNames from 'classnames';
import { DataDictionaryBulkEditModal } from './DataDictionaryBulkEditModal';
import { getClosestSelectionTable } from '@lib/popups';
import { getSonarObjectTypeLabelPlural } from '@sonar-software/sonar-ts-lib';
import { KeyPagination } from '@ui/KeyPagination';
import { SelectionTable } from '@ui/tables';
import { PaginationDirection, SonarObjectType } from '@sonar-software/sonar-ts-types';
import { parseMetadataFromResponse, translateFilterQueryParamsToDynamicQueryLanguage, translateOrderQueryParamsToDynamicQueryLanguage } from '@ui/table-modifier/@arch/lib';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { TableModifier } from '@ui/table-modifier';
import { useFeatureFlags, useOnUnmount, usePeriscopeActions, usePeriscopeCount, usePrevious, useSelectionTable } from '@hooks';
const convertToDql = (queryParams) => {
    const { filterCount, sortBy, sortDefault, sortDirection } = queryParams, rest = __rest(queryParams, ["filterCount", "sortBy", "sortDefault", "sortDirection"]);
    return {
        filters: translateFilterQueryParamsToDynamicQueryLanguage(rest),
        ordering: translateOrderQueryParamsToDynamicQueryLanguage(queryParams),
        paginationDirection: PaginationDirection.Forward
    };
};
function getResourceIdsUri(queryParams) {
    switch (true) {
        case Boolean(queryParams.applicationId):
            return `v3/data-dictionary/applications/${queryParams.applicationId}/bulk`;
        case Boolean(queryParams.tagId):
            return `v3/data-dictionary/tags/${queryParams.tagId}/bulk`;
        case Boolean(queryParams.sonarObjectId):
            return `v3/data-dictionary/fields/${queryParams.sonarObjectId}/bulk`;
        case Boolean(queryParams.sonarObjectType):
            return `v3/data-dictionary/sonar-objects/types/${queryParams.sonarObjectType}/bulk`;
        case typeof queryParams.complianceCategory === 'number':
            return `v3/data-dictionary/compliance-categories/${queryParams.complianceCategory}/bulk`;
        default:
            return '';
    }
}
function getRowKey(entry) {
    return entry.sonarObjectId;
}
export const DataDictionaryTable = ({ loading, queryParams, resource, updateQuery }) => {
    var _a, _b;
    const [showEditModal, setShowEditModal] = useState(false);
    const editButtonRef = useRef(null);
    const { openPeriscope } = usePeriscopeActions();
    const periscopeCount = usePeriscopeCount();
    const { isEnabled } = useFeatureFlags();
    const sonarObjectTypeOrField = (_a = queryParams.sonarObjectType) !== null && _a !== void 0 ? _a : SonarObjectType.SalesforceField;
    const previousSonarObjectId = usePrevious(queryParams.sonarObjectId);
    const previousSonarObjectType = usePrevious(queryParams.sonarObjectType);
    const tableDataChanged = queryParams.sonarObjectId !== previousSonarObjectId || queryParams.sonarObjectType !== previousSonarObjectType;
    const scrollableListRef = useRef(null);
    const selectionDataRef = useRef(null);
    const handleRowClick = useCallback((record) => ({
        onClick: () => {
            const { name, sonarObjectId: recordSonarObjectId } = record;
            openPeriscope({
                id: recordSonarObjectId,
                name: name,
                openedFromIndex: -1,
                type: sonarObjectTypeOrField
            });
        }
    }), [openPeriscope, sonarObjectTypeOrField]);
    const { columns, filters, schema, sortOptions } = useMemo(() => parseMetadataFromResponse(resource === null || resource === void 0 ? void 0 : resource.schema, isEnabled, queryParams.columns, getClosestSelectionTable), [isEnabled, queryParams.columns, resource]);
    selectionDataRef.current = useSelectionTable({
        getRowKey,
        namespace: 'dataDictionary',
        queryParams: queryParams,
        resourceIdsUri: getResourceIdsUri(queryParams),
        results: resource === null || resource === void 0 ? void 0 : resource.results,
        selectionType: sonarObjectTypeOrField
    });
    useLayoutEffect(() => {
        var _a, _b;
        const header = (_a = scrollableListRef.current) === null || _a === void 0 ? void 0 : _a.getElementsByClassName('ant-table-header')[0];
        if (header) {
            const height = (_b = header.firstElementChild) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect().height;
            if (height) {
                header.style.minHeight = `${height - 1}px`;
            }
        }
    });
    useOnUnmount(() => {
        var _a;
        (_a = selectionDataRef.current) === null || _a === void 0 ? void 0 : _a.resetSelection();
    });
    useEffect(() => {
        var _a, _b;
        if (((_a = selectionDataRef.current) === null || _a === void 0 ? void 0 : _a.totalCount) && tableDataChanged) {
            (_b = selectionDataRef.current) === null || _b === void 0 ? void 0 : _b.resetSelection();
        }
    }, [queryParams, selectionDataRef, tableDataChanged]);
    const handleEditModalOpen = useCallback((button) => {
        editButtonRef.current = button;
        setShowEditModal(true);
    }, []);
    const handleEditModalCancel = useCallback(() => {
        setShowEditModal(false);
    }, []);
    const handleEditModalSuccess = useCallback(() => {
        var _a;
        (_a = selectionDataRef.current) === null || _a === void 0 ? void 0 : _a.resetSelection();
        setShowEditModal(false);
    }, [selectionDataRef]);
    const canClick = queryParams.sonarObjectType !== SonarObjectType.SalesforceProfile;
    const dataDictionaryTableClassnames = classNames('data-dictionary-table', {
        'no-click': !canClick,
        'periscope-open': periscopeCount === 1
    });
    return (React.createElement(React.Fragment, null,
        Boolean(resource) &&
            React.createElement(TableModifier, { allowColumnConfig: true, columns: columns, columnsString: queryParams.columns, convertToDynamicQueryLanguage: convertToDql, filterDisplayType: 'menu', filters: filters, filtersString: queryParams.filters, onChange: updateQuery, orderString: queryParams.ordering, sonarObjectId: queryParams.sonarObjectId, sonarObjectType: queryParams.sonarObjectType, sortOptions: sortOptions }),
        React.createElement(SelectionTable, { className: dataDictionaryTableClassnames, columns: schema, dataSource: resource === null || resource === void 0 ? void 0 : resource.results, forceShowSelection: canClick ? undefined : false, loading: loading, onEditSelection: handleEditModalOpen, onRow: canClick ? handleRowClick : undefined, ref: scrollableListRef, rowKey: getRowKey, scroll: {
                scrollToFirstRowOnChange: true,
                x: (resource === null || resource === void 0 ? void 0 : resource.results.length) ? 'max-content' : 4000
            }, selectionData: selectionDataRef.current, selectionType: getSonarObjectTypeLabelPlural(sonarObjectTypeOrField) }),
        React.createElement(KeyPagination, { defaultPageSize: Number(queryParams.count), listRef: scrollableListRef, onChange: updateQuery, resource: resource }),
        React.createElement(DataDictionaryBulkEditModal, { onCancel: handleEditModalCancel, onOk: handleEditModalSuccess, open: showEditModal, popoverTarget: editButtonRef, selected: (_b = selectionDataRef.current) === null || _b === void 0 ? void 0 : _b.selectedKeys, sonarObjectType: sonarObjectTypeOrField })));
};
